import { FC, useContext, useEffect, useRef, useState } from 'react'
import jsLogger from 'js-logger'
import LiveKitAvatar, { ILiveKitAvatar } from 'shared/components/LiveKitAvatar'
import Recorder from 'shared/components/Recorder'
import ServerTranscriber from 'shared/components/ServerTranscriber'
import { getBackClient } from 'shared/controllers/back'
import { dbSendLatencies } from 'controllers/main'
import { SessionLatencies } from 'types/internal'
import { Proto } from 'types/demo3'
import { AvatarSessionBootstrap, Demo2ResponseResult } from 'types/back'
import ConnnectionContext from 'shared/contexts/ConnectionContext'
import StreamContext from 'shared/contexts/StreamContext'
import { useGetSet } from 'react-use'
import Impostor from 'components/Impostor'

// Demo3 is fully server-authoritative: the socket server owns STT, turn-taking,
// barge-in, phrase generation, avatar provisioning + voicing and the session
// lifecycle. This page is a THIN client — it renders the server-provisioned
// avatar, streams the mic to the server for STT (ServerTranscriber), records the
// user camera, and reflects the little state the server relays (interim
// transcript, recognized userName, providedUserDetected). It does NOT run any
// client-side conversation control and does NOT voice the avatar.

type Props = {
  sessionInfo: Proto.ConversationStartResponse
  started: boolean
  onError: (message: string) => void
  onIdleTimeout?: () => void
}

const Conversation: FC<Props> = ({
  sessionInfo,
  started,
  onError,
  onIdleTimeout
}) => {
  const { updateConnectionStatus } = useContext(ConnnectionContext)
  const { stream } = useContext(StreamContext)
  const avatarRef = useRef<ILiveKitAvatar>(null)
  const [avatarInitialized, setAvatarInitialized] = useState(false)
  // One-shot guard: the greeting is cued to the server exactly once, when the
  // avatar is ready, the session has started, and the user stream exists.
  const greetingCuedRef = useRef(false)
  const [getImpostorVisible, setImpostorVisible] = useGetSet(false)

  // Server-minted LiveKit/avatar creds. Null while provisioning is in flight;
  // null after provisioning finishes means it failed — there is no client-side
  // fallback, so the avatar stays on its still photo.
  const [avatarSession, setAvatarSession] =
    useState<AvatarSessionBootstrap | null>(null)
  const [avatarProvisionDone, setAvatarProvisionDone] = useState(false)

  const { avatarImageUrl } = sessionInfo.sessionSettings

  useEffect(() => {
    jsLogger.log('CONVERSATION START')
    const back = getBackClient()

    // Server-side STT: interim transcript. Demo3 has no subtitle ControlPanel,
    // so we only log it.
    back.sttInterimHandler = (transcript: string) => onInterim(transcript)

    // The server drives the whole turn and pushes each generated phrase. Demo3
    // has no subtitle ControlPanel — we only track the recognized userName.
    back.serverPhraseHandler = (response: Demo2ResponseResult) => {
      jsLogger.log('Conversation: serverPhrase', {
        text: response?.q?.text,
        over: response?.isOver,
        userName: response?.userName
      })
    }

    // Demo3-specific: the server signals when the provided (registered) user is
    // detected in-frame → dismiss the impostor overlay.
    back.providedUserDetectedHandler = onProvidedUserDetected

    // Demo3 barge-in stays disabled: server turn control, no interruption mode.
    back.emitTurnControl('server')

    // Ask the server to mint the LiveKit room + avatar session and hand back the
    // browser bootstrap; the mount is gated on it (renderAvatar). No client
    // fallback → still photo on failure.
    back
      .provisionAvatar()
      .then((session) => setAvatarSession(session))
      .catch(() => setAvatarSession(null))
      .finally(() => setAvatarProvisionDone(true))
  }, [])

  // Cue the greeting exactly once, when the avatar has initialized AND the
  // session has started AND the user stream exists (audio playout unlocked).
  // This replaces demo3's old onSessionStarted → updateSessionState().
  useEffect(() => {
    if (avatarInitialized && started && stream && !greetingCuedRef.current) {
      greetingCuedRef.current = true
      jsLogger.log('Conversation: cueing server greeting')
      getBackClient().emitAvatarReady()
    }
  }, [avatarInitialized, started, stream])

  const onInterim = (v: string) => {
    jsLogger.log('Conversation: sttInterim', { transcript: v })
  }

  const onProvidedUserDetected = () => {
    jsLogger.log('Conversation: providedUserDetected → hide impostor')
    setImpostorVisible(false)
  }

  const onAvatarStarted = () => {
    jsLogger.log('Conversation: avatar started')
    setAvatarInitialized(true)
  }

  const onHGLatencies = (
    full: number,
    streamingNew: number,
    connectionPreparing: number,
    websocket: number
  ) => {
    const latencies: Partial<SessionLatencies> = {
      heyGenInitialization: full,
      heyGenStreamingNew: streamingNew,
      heyGenConnectionPreparing: connectionPreparing,
      heyGenWebsocket: websocket
    }
    dbSendLatencies(sessionInfo.sessionId, latencies)
  }

  const onTranscriberConnected = (latency: number, connectionNum: number) => {
    const latencies: Partial<SessionLatencies> = {}
    if (connectionNum === 1) {
      latencies.transcriberConnection = latency
      latencies.transcriberConnectionsAmount = 1
    } else {
      latencies.transcriberConnectionsAmount = connectionNum
    }
    dbSendLatencies(sessionInfo.sessionId, latencies)
  }

  const onHGConnectionStatusChanged = (isConnected: boolean) => {
    updateConnectionStatus('HG', isConnected)
  }

  const onDGConnectionStatusChanged = (isConnected: boolean) => {
    updateConnectionStatus('DG', isConnected)
  }

  const onImpostorContinueClick = () => {
    jsLogger.log('Conversation: impostor continue clicked')
    setImpostorVisible(false)
  }

  const renderAvatar = () => {
    // Wait for provisioning to resolve before mounting. While we wait — and if
    // it fails (no client fallback) — show the avatar's still photo (same
    // container/layout as LiveKitAvatar) instead of a blank screen.
    if (!avatarProvisionDone || !avatarSession) {
      return (
        <div className='h-full w-full flex flex-col justify-between flex-1 relative bg-gray-700'>
          {avatarImageUrl && (
            <img
              src={avatarImageUrl}
              alt='Avatar'
              className='absolute w-full h-full object-cover left-0 top-0 right-0 bottom-0'
            />
          )}
        </div>
      )
    }
    return (
      <LiveKitAvatar
        ref={avatarRef}
        avatarSession={avatarSession}
        userStream={stream}
        onSessionStarted={onAvatarStarted}
        avatarImageUrl={avatarImageUrl}
        onError={onError}
        trackInitializationLatencies={onHGLatencies}
        onConnectionStatusChanged={onHGConnectionStatusChanged}
        onIdleTimeout={onIdleTimeout}
      />
    )
  }

  const renderImpostor = () => {
    if (getImpostorVisible()) {
      return <Impostor onContinue={onImpostorContinueClick} />
    }
  }

  return (
    <>
      {renderAvatar()}
      <div
        className={
          'flex-1 bg-slate-100 rounded-md absolute top-[3rem] left-5 w-28 h-44 transition-all duration-500 ease-in-out'
        }
      >
        <Recorder onError={onError} />
      </div>
      <ServerTranscriber
        onRawAudio={(pcm, sampleRate) =>
          getBackClient().emitAudioChunk(pcm, sampleRate)
        }
        onConnected={onTranscriberConnected}
        onConnectionStatusChanged={onDGConnectionStatusChanged}
      />
      {renderImpostor()}
    </>
  )
}

export default Conversation
